import { createSelector } from "reselect";
import { RootState } from "store";

export const getActivePlan = (state: RootState) =>
    state.membershipState.membership?.data?.active_plan;

export const isTrialPlan = createSelector(
    [getActivePlan],
    (activePlan) => !!activePlan?.plan_frequency?.is_trial
);

export const planPeriod = createSelector(
    [getActivePlan],
    (activePlan) => {
        if (!activePlan?.start_date || !activePlan?.end_date) {
            return {
                totalPlanDays: 0,
                planDaysLeft: 0,
                currentPlanDay: 0,
                planDaysCompletedPercentage: 0,
                endsIn: null,
                state: "expired",
            };
        }

        const startDate = new Date(activePlan.start_date);
        const endDate = new Date(activePlan.end_date);
        const nowDate = new Date();
       
        /* ---------------- TEST CASES ---------------- */

        // 1️⃣ TEST: Plan expired (after end date)
        // nowDate.setTime(endDate.getTime() + 24 * 60 * 60 * 1000);

        // 2️⃣ TEST: Last day of plan
        // nowDate.setTime(endDate.getTime());

        // 3️⃣ TEST: 5 hours before end_date
        // nowDate.setTime(endDate.getTime() - 5 * 60 * 60 * 1000);

        // 4️⃣ TEST: 30 minutes before end_date
        // nowDate.setTime(endDate.getTime() - 30 * 60 * 1000);

        // 5️⃣ TEST: 10 seconds before end_date
        // nowDate.setTime(endDate.getTime() - 10 * 1000);

        // 6️⃣ TEST: 1 day before last day
        // nowDate.setTime(endDate.getTime() - 1 * 24 * 60 * 60 * 1000);

        // 7️⃣ TEST: 2 days before last day
        // nowDate.setTime(endDate.getTime() - 2 * 24 * 60 * 60 * 1000);

        // 8️⃣ TEST: 4 days before last day
        // nowDate.setTime(endDate.getTime() - 4 * 24 * 60 * 60 * 1000);

        // 9️⃣ TEST: Mid plan
        // nowDate.setTime(
        //     startDate.getTime() +
        //         (endDate.getTime() - startDate.getTime()) / 2
        // );

        /* -------------------------------------------- */

        const msInDay = 1000 * 60 * 60 * 24;

        const startDay = new Date(startDate);
        const endDay = new Date(endDate);
        const today = new Date(nowDate);

        startDay.setHours(0, 0, 0, 0);
        endDay.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const totalPlanDays = Math.floor(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
          );

        const currentPlanDay = Math.min(
            totalPlanDays,
            Math.max(
                1,
                Math.floor((today.getTime() - startDay.getTime()) / msInDay) + 1
            )
        );

        const planDaysLeft = Math.max(0, totalPlanDays - currentPlanDay);

        const planDaysCompletedPercentage =
            (currentPlanDay / totalPlanDays) * 100;


        let endsIn: string | null = null;

        const remainingMs = endDate.getTime() - nowDate.getTime();

        if (remainingMs > 0 && remainingMs < msInDay) {
            const totalSeconds = Math.floor(remainingMs / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const hours = Math.floor(minutes / 60);

            if (hours > 0) {
                endsIn = `Ends in ${hours} hour${hours > 1 ? "s" : ""}`;
            } else if (minutes > 0) {
                endsIn = `Ends in ${minutes} min`;
            } else {
                endsIn = `Ends in ${totalSeconds} sec`;
            }
        }


        let state: "safe" | "warning" | "critical" | "expired";

        if (planDaysLeft > 3) state = "safe";
        else if (planDaysLeft > 1) state = "warning";
        else if (planDaysLeft > 0 || endsIn) state = "critical";
        else state = "expired";

        return {
            totalPlanDays,
            planDaysLeft,
            currentPlanDay,
            planDaysCompletedPercentage,
            endsIn,
            state,
        };
    }
);